import type { Container } from '@tsparticles/engine';
import { Particles, initParticlesEngine } from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import { motion, useAnimation } from 'motion/react';
import React, { useEffect, useMemo, useState } from 'react';

import { getSparklesOptions } from '@/utils/sparkles-config.ts';
import { cn } from '@/utils/style-utils.ts';

type Props = {
    className?: string;
    background?: string;
    particleSize?: number;
    minSize?: number;
    maxSize?: number;
    speed?: number;
    particleColor?: string;
    particleDensity?: number;
};

export const SparklesCore = ({
    className,
    background,
    minSize,
    maxSize,
    speed,
    particleColor,
    particleDensity,
}: Props) => {
    const [init, setInit] = useState(false);
    useEffect(() => {
        const setup = async (): Promise<void> => {
            await initParticlesEngine(async (engine) => {
                await loadSlim(engine);
            });
            setInit(true);
        };
        void setup();
    }, []);
    const controls = useAnimation();

    const particlesLoaded = async (container?: Container): Promise<void> => {
        if (container) {
            await controls.start({
                opacity: 1,
                transition: {
                    duration: 1,
                },
            });
        }
    };

    // useMemo justified: getSparklesOptions returns a large object literal; a new reference triggers tsparticles re-initialization
    const options = useMemo(
        () => getSparklesOptions({ background, minSize, maxSize, speed, particleColor, particleDensity }),
        [background, minSize, maxSize, speed, particleColor, particleDensity]
    );

    return (
        <motion.div animate={controls} className={cn('opacity-0', className)}>
            {init && <Particles className={cn('h-full w-full')} particlesLoaded={particlesLoaded} options={options} />}
        </motion.div>
    );
};
